import { Grid, Autocomplete, TextField, Box, Button, Chip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import useTraffic from '../useTraffic';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import TooltipIconButton from '../../../components/TooltipIconButton';
import { Delete } from '@mui/icons-material';
import SimpleRadioGroup from '../../../components/RadioGroup';
import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';
import { Country, State, City, Location } from '../types';

type Props = {
  onChange: (param: { locations: Location[] }) => any;
  value: {
    type?: 'blacklist' | 'whitelist';
    locations: Location[];
  };
};

type CountryOption = Country | { id: string; name: string; iso2?: string; iso3?: string };
type StateOption = State | { id: string; name: string; state_code?: string };
type CityOption = City | { id: string; name: string };

const CountrySelector = ({ onChange, value }: Props) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [selectedType, setSelectedType] = useState<'blacklist' | 'whitelist'>('blacklist');
  const [selectedCountry, setSelectedCountry] = useState<Country | 'all'>('all');
  const [selectedState, setSelectedState] = useState<State | 'all'>('all');
  const [selectedCity, setSelectedCity] = useState<City | 'all'>('all');

  const locations = value.locations;
  const client = useTraffic();

  const initCountries = async () => {
    try {
      const res = await client.getCountries().call();
      setCountries(res);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const initStates = async (countryId: number) => {
    try {
      const res = await client.getStates().call(countryId);
      setStates(res);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const initCities = async (countryId: number, stateId: number) => {
    try {
      const res = await client.getCities().call(countryId, stateId);
      setCities(res);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const onCountryChange = (_event: any, newValue: CountryOption | null) => {
    const val = newValue ?? 'all';
    setSelectedCountry(val === 'all' ? 'all' : (val as Country));
    setSelectedState('all');
    setSelectedCity('all');
    setStates([]);
    setCities([]);
    if (val !== 'all') {
      initStates(val.id as number);
    }
  };

  const onStateChange = (_event: any, newValue: StateOption | null) => {
    const val = newValue ?? 'all';
    setSelectedState(val === 'all' ? 'all' : (val as State));
    setSelectedCity('all');
    setCities([]);
    
    if (val !== 'all' && typeof val === 'object' && typeof val.id === 'number' && selectedCountry !== 'all') {
      initCities(selectedCountry.id, val.id);
    }
  };

  const onCityChange = (_event: any, newValue: CityOption | null) => {
    const val = newValue ?? 'all';
    setSelectedCity(val === 'all' ? 'all' : (val as City));
  };

  const onTypeChange = (newValue: string) => {
    setSelectedType(newValue as 'blacklist' | 'whitelist');
  };

  const addLocation = () => {
    const id = nanoid();
    const val: Location = {
      country: selectedCountry,
      state: selectedState,
      city: selectedCity,
      type: selectedType,
      id,
    };
    const newLocations = cloneDeep([...locations, val]);
    onChange({ locations: newLocations });
  };

  const deleteLocation = (id: string) => {
    const res = locations.filter((l) => l.id !== id);
    onChange({ locations: res });
  };

  const getRowId = (r: Location) => `${r.id}`;

  const cols: GridColDef<Location>[] = [
    {
      field: 'country',
      headerName: 'Country',
      flex: 1,
      valueGetter: ({ row }) => {
        const { country } = row;
        if (country === 'all') return 'All countries';
        return country?.name ?? '';
      },
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1,
      valueGetter: ({ row }) => {
        const { state } = row;
        if (state === 'all') return 'All states';
        return state?.name ?? '';
      },
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 1,
      valueGetter: ({ row }) => {
        const { city } = row;
        if (city === 'all') return 'All cities';
        return city?.name ?? '';
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      renderCell: (params) => {
        const val = params.row.type;
        return val ? <Chip label={val} /> : <Chip label="None" color="error" />;
      },
    },
    {
      field: '',
      headerName: 'Actions',
      flex: 1,
      renderCell: ({ row }) => (
        <TooltipIconButton
          title="Delete"
          icon={<Delete />}
          onClick={() => deleteLocation(row.id)}
        />
      ),
    },
  ];

  const fields = [
    { value: 'whitelist', label: 'Whitelist' },
    { value: 'blacklist', label: 'Blacklist' },
  ];

  useEffect(() => {
    initCountries();
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={9}>
        <DataGrid
          columns={cols}
          rows={locations}
          getRowId={getRowId}
          sx={{ height: 500 }}
        />
      </Grid>
      <Grid item xs={3}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box mb={2}>
            <SimpleRadioGroup
              horizontal
              fields={fields}
              label="Block type"
              onChange={onTypeChange}
              value={selectedType}
            />
          </Box>
          <Autocomplete
            options={[{ id: 'all', name: 'All countries', iso2: '', iso3: '' } as CountryOption, ...countries]}
            getOptionLabel={(option) => option.name}
            value={selectedCountry === 'all' ? { id: 'all', name: 'All countries', iso2: '', iso3: '' } : selectedCountry}
            onChange={onCountryChange}
            renderInput={(params) => <TextField {...params} label="Country" fullWidth />}
            filterOptions={(options, { inputValue }) =>
              options.filter((option) =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Autocomplete
            options={[{ id: 'all', name: 'All states', state_code: '' } as StateOption, ...states]}
            getOptionLabel={(option) => option.name}
            value={selectedState === 'all' ? { id: 'all', name: 'All states', state_code: '' } : selectedState}
            onChange={onStateChange}
            disabled={!states.length || selectedCountry === 'all'}
            renderInput={(params) => <TextField {...params} label="State" fullWidth />}
            filterOptions={(options, { inputValue }) =>
              options.filter((option) =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Autocomplete
            options={[{ id: 'all', name: 'All cities' } as CityOption, ...cities]}
            getOptionLabel={(option) => option.name}
            value={selectedCity === 'all' ? { id: 'all', name: 'All cities' } : selectedCity}
            onChange={onCityChange}
            disabled={!cities.length || selectedState === 'all'}
            renderInput={(params) => <TextField {...params} label="City" fullWidth />}
            filterOptions={(options, { inputValue }) =>
              options.filter((option) =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Button variant="contained" onClick={addLocation}>
            Add
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CountrySelector;